<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  botAutonomy: {
    type: Object,
    default: () => ({ bot_resolved: 0 }),
  },
  loading: { type: Boolean, default: false },
});

const { t } = useI18n();

const HUMAN_COST_PER_TICKET = 8.5;
const AI_COST_PER_TICKET = 0.12;

const calculations = computed(() => {
  const aiTickets = props.botAutonomy.bot_resolved || 0;
  const humanCost = aiTickets * HUMAN_COST_PER_TICKET;
  const aiCost = aiTickets * AI_COST_PER_TICKET;
  const savings = humanCost - aiCost;
  const roi = aiCost > 0 ? ((savings / aiCost) * 100).toFixed(0) : 0;

  return {
    aiTickets,
    humanCost: humanCost.toFixed(0),
    aiCost: aiCost.toFixed(0),
    savings: savings.toFixed(0),
    roi,
    monthlySavings: (savings * (30 / 7)).toFixed(0),
  };
});

const formatDollars = val => `$${Number(val).toLocaleString()}`;
</script>

<template>
  <div
    class="flex flex-col rounded-xl shadow outline-1 outline outline-n-container bg-n-solid-2 px-6 py-5"
  >
    <h3 class="mb-4 text-sm font-medium text-n-slate-11 uppercase tracking-wider">
      {{ t('CEO_DASHBOARD.ROI.HEADER') }}
    </h3>
    <div v-if="loading" class="flex items-center justify-center py-8">
      <Spinner />
    </div>
    <div v-else class="space-y-3">
      <div
        class="flex items-baseline justify-between py-2 border-b border-n-container"
      >
        <span class="text-sm text-n-slate-11">
          {{ t('CEO_DASHBOARD.ROI.AI_TICKETS') }}
        </span>
        <span class="text-sm font-semibold text-n-slate-12">
          {{ calculations.aiTickets.toLocaleString() }}
        </span>
      </div>
      <div
        class="flex items-baseline justify-between py-2 border-b border-n-container"
      >
        <span class="text-sm text-n-slate-11">
          {{ t('CEO_DASHBOARD.ROI.HUMAN_COST') }}
        </span>
        <span class="text-sm text-n-ruby-11">
          {{ formatDollars(calculations.humanCost) }}
        </span>
      </div>
      <div
        class="flex items-baseline justify-between py-2 border-b border-n-container"
      >
        <span class="text-sm text-n-slate-11">
          {{ t('CEO_DASHBOARD.ROI.AI_COST') }}
        </span>
        <span class="text-sm text-n-teal-11">
          {{ formatDollars(calculations.aiCost) }}
        </span>
      </div>
      <div
        class="flex items-baseline justify-between py-3 rounded-lg bg-n-teal-3 px-3 -mx-1"
      >
        <span class="text-sm font-semibold text-n-teal-11">
          {{ t('CEO_DASHBOARD.ROI.NET_SAVINGS') }}
        </span>
        <span class="text-xl font-bold text-n-teal-11">
          {{ formatDollars(calculations.savings) }}
        </span>
      </div>
      <div class="flex items-baseline justify-between pt-2">
        <span class="text-xs text-n-slate-10">
          {{ t('CEO_DASHBOARD.ROI.PROJECTED_MONTHLY') }}
        </span>
        <span class="text-sm font-semibold text-n-teal-11">
          {{ formatDollars(calculations.monthlySavings) }}/mo
        </span>
      </div>
      <div class="flex items-baseline justify-between">
        <span class="text-xs text-n-slate-10">
          {{ t('CEO_DASHBOARD.ROI.ROI_PERCENTAGE') }}
        </span>
        <span class="text-sm font-bold text-n-teal-11">
          {{ calculations.roi }}%
        </span>
      </div>
    </div>
  </div>
</template>
